import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AdvertiserReportSpecificationOutputDto,
  LookerAdvertiserReportScheduleInputDto,
  PagedModelReportScheduleOutputDto,
  ReportDataDto,
  ReportOutputDto,
  ReportScheduleHistoryDto,
} from '@kortxio/hub-api';
import { VisualizationQueryResult } from '@kortxio/hub-api';
import { setError } from 'features/error/slice';
import { ReportBuilderRunQueryParams } from 'features/reports/advertisers/types';
import api from 'libs/api';
import { parseApiPayload, toApiError } from 'libs/api/helper';

export const getAllReportsForAdvertiser = createAsyncThunk<
  ReportOutputDto[],
  {
    clientId: number;
    advertiserId: number;
  }
>(
  'advertiserReports/getAllReportsForAdvertiser',
  async ({ clientId, advertiserId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.advertiserReports.getAllReportsForAdvertiser(
        clientId,
        advertiserId
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAllAdvertiserReportSpecificationsForAdvertiser =
  createAsyncThunk<
    AdvertiserReportSpecificationOutputDto[],
    {
      clientId: number;
      advertiserId: number;
    }
  >(
    'advertiserReports/getAdvertiserReportSpecificationsForClientByParams',
    async ({ clientId, advertiserId }, { dispatch, rejectWithValue }) => {
      try {
        const response =
          await api.advertiserReports.getAdvertiserReportSpecificationsForClientByParams(
            clientId,
            [advertiserId]
          );

        return parseApiPayload(response);
      } catch (error) {
        const apiError = toApiError(error);

        if (!apiError.isAbortedError) {
          dispatch(
            setError({
              message: apiError.message,
              uiMessage: apiError.uiMessage,
            })
          );
        }

        return rejectWithValue(apiError);
      }
    }
  );

export const runQueryForBuilderReport = createAsyncThunk<
  VisualizationQueryResult,
  ReportBuilderRunQueryParams
>(
  'advertiserReports/runQueryForBuilderReport',
  async (
    { advertiserId, reportName, query, tag },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await api.reportVisualization.queryReportsByAdvertiser(
        advertiserId,
        reportName,
        query,
        tag
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAllReportSchedulesForAdvertiserAndBuilderReport =
  createAsyncThunk<
    PagedModelReportScheduleOutputDto,
    {
      clientId: number;
      advertiserId: number;
      id?: string;
    }
  >(
    'advertiserReports/getAllReportSchedulesForAdvertiserAndBuilderReport',
    async ({ clientId, advertiserId, id }, { dispatch, rejectWithValue }) => {
      try {
        const response =
          await api.advertiserReports.getAllReportSchedulesForAdvertiser(
            clientId,
            advertiserId,
            'LOOKER',
            id
          );

        return parseApiPayload(response);
      } catch (error) {
        const apiError = toApiError(error);

        if (!apiError.isAbortedError) {
          dispatch(
            setError({
              message: apiError.message,
              uiMessage: apiError.uiMessage,
            })
          );
        }

        return rejectWithValue(apiError);
      }
    }
  );

export const createLookerReportScheduleForAdvertiser = createAsyncThunk<
  ReportDataDto,
  {
    clientId: number;
    advertiserId: number;
    id: string;
    requestBody: LookerAdvertiserReportScheduleInputDto;
  }
>(
  'advertiserReports/createLookerReportScheduleForAdvertiser',
  async (
    { clientId, advertiserId, id, requestBody },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response =
        await api.advertiserReports.postReportScheduleForAdvertiser(
          clientId,
          advertiserId,
          id,
          requestBody
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
            uiFieldErrorMessages: apiError.uiFieldErrorMessages,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const deleteReportScheduleForAdvertiser = createAsyncThunk<
  ReportDataDto,
  {
    clientId: number;
    advertiserId: number;
    scheduleId: string;
  }
>(
  'advertiserReports/deleteReportScheduleForAdvertiser',
  async (
    { clientId, advertiserId, scheduleId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response =
        await api.advertiserReports.deleteReportScheduleForAdvertiser(
          clientId,
          advertiserId,
          scheduleId
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getReportScheduleHistoryByIdForAdvertiser = createAsyncThunk<
  ReportScheduleHistoryDto,
  {
    clientId: number;
    advertiserId: number;
    scheduleId: string;
  }
>(
  'advertiserReports/getReportScheduleHistoryByIdForAdvertiser',
  async (
    { clientId, advertiserId, scheduleId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response =
        await api.advertiserReports.getReportScheduleHistoryByIdForAdvertiser(
          clientId,
          advertiserId,
          scheduleId
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);
